export interface Perfil {
  id: string
  nombres: string
  apellidos: string
  correo: string
  celular: string | null
  version: number
  roles: string[]
}

interface Problema {
  title?: string
  detail?: string
}

export class ErrorApi extends Error {
  public constructor(
    mensaje: string,
    public readonly estado: number,
    public readonly codigo?: string,
  ) {
    super(mensaje)
  }
}

function obtenerCsrf(): string {
  const prefijo = 'csrf='
  const encontrada = document.cookie
    .split(';')
    .map((valor) => valor.trim())
    .find((valor) => valor.startsWith(prefijo))
  return encontrada ? decodeURIComponent(encontrada.slice(prefijo.length)) : ''
}

export function tieneSesionCsrf(): boolean {
  return Boolean(obtenerCsrf())
}

export function normalizarContrasenaPegada(contrasena: string): string {
  return contrasena
    .replace(/[\u200B-\u200D\uFEFF]/gu, '')
    .replace(/\u00A0/gu, ' ')
    .trim()
}

export async function solicitar<T>(
  ruta: string,
  opciones: RequestInit = {},
  permitirRenovacion = true,
): Promise<T> {
  const metodo = opciones.method ?? 'GET'
  const mutacion = !['GET', 'HEAD'].includes(metodo)
  const esFormulario = typeof FormData !== 'undefined' && opciones.body instanceof FormData
  let respuesta = await fetch(ruta, {
    ...opciones,
    // Las respuestas de la API contienen el estado actual de la sesión y del
    // expediente. Evitamos revalidaciones condicionales (304) que no incluyen
    // cuerpo JSON y que el cliente no puede procesar como una respuesta normal.
    cache: 'no-store',
    credentials: 'include',
    headers: {
      ...(opciones.body && !esFormulario ? { 'content-type': 'application/json' } : {}),
      ...(mutacion && obtenerCsrf() ? { 'x-csrf-token': obtenerCsrf() } : {}),
      ...opciones.headers,
    },
  })
  // Algunos navegadores conservan un ETag de una sesión anterior. Un 304 no
  // contiene JSON para esta API, por lo que reintentamos una lectura GET sin
  // caché para entregar el recurso completo al módulo que lo solicitó.
  if (respuesta.status === 304 && !mutacion) {
    const separador = ruta.includes('?') ? '&' : '?'
    respuesta = await fetch(`${ruta}${separador}_sin_cache=${Date.now()}`, {
      ...opciones,
      cache: 'no-store',
      credentials: 'include',
      headers: {
        ...opciones.headers,
      },
    })
  }
  if (!respuesta.ok) {
    if (
      respuesta.status === 401 &&
      permitirRenovacion &&
      ruta !== '/api/v1/autenticacion/renovar' &&
      ruta !== '/api/v1/autenticacion/iniciar'
    ) {
      try {
        await renovarSesion()
        return solicitar<T>(ruta, opciones, false)
      } catch {
        window.dispatchEvent(new CustomEvent('sesion-expirada'))
      }
    }
    const problema = (await respuesta.json().catch(() => ({}))) as Problema
    if (respuesta.status === 401) window.dispatchEvent(new CustomEvent('sesion-expirada'))
    throw new ErrorApi(
      problema.detail ?? 'No fue posible completar la solicitud.',
      respuesta.status,
      problema.title,
    )
  }
  return respuesta.status === 204 ? (undefined as T) : ((await respuesta.json()) as T)
}

let renovacionEnCurso: Promise<void> | null = null
export function renovarSesion(): Promise<void> {
  if (!renovacionEnCurso) {
    renovacionEnCurso = solicitar<void>('/api/v1/autenticacion/renovar', {
      method: 'POST',
    })
      .then(() => {
        window.dispatchEvent(new CustomEvent('sesion-renovada'))
      })
      .finally(() => {
        renovacionEnCurso = null
      })
  }
  return renovacionEnCurso!
}

export async function iniciarSesion(correo: string, contrasena: string): Promise<void> {
  await solicitar('/api/v1/autenticacion/iniciar', {
    method: 'POST',
    body: JSON.stringify({ correo, contrasena: normalizarContrasenaPegada(contrasena) }),
  })
}

export function consultarPerfil(): Promise<Perfil> {
  return solicitar('/api/v1/mi-perfil')
}

export function actualizarPerfil(entrada: {
  correo: string
  celular?: string
  version: number
}): Promise<Perfil> {
  return solicitar('/api/v1/mi-perfil', { method: 'PATCH', body: JSON.stringify(entrada) })
}

export function cambiarContrasena(actual: string, nueva: string): Promise<void> {
  return solicitar('/api/v1/mi-perfil/contrasena', {
    method: 'PATCH',
    body: JSON.stringify({
      actual: normalizarContrasenaPegada(actual),
      nueva: normalizarContrasenaPegada(nueva),
    }),
  })
}

export function cerrarSesion(): Promise<void> {
  return solicitar('/api/v1/autenticacion/sesion', { method: 'DELETE' })
}

export function solicitarRecuperacion(correo: string): Promise<void> {
  return solicitar('/api/v1/autenticacion/recuperacion', {
    method: 'POST',
    body: JSON.stringify({ correo }),
  })
}

export function restablecerContrasena(token: string, contrasenaNueva: string): Promise<void> {
  return solicitar('/api/v1/autenticacion/restablecimiento', {
    method: 'POST',
    body: JSON.stringify({ token, contrasenaNueva: normalizarContrasenaPegada(contrasenaNueva) }),
  })
}
